import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  GetEngineeringTicketPlatformIntegrationQuery,
  useUpdateEngineeringTicketPlatformIntegrationMutation,
} from "../../__generated__/graphql";
import { useUnsavedChanges } from "../../contexts/UnsavedChangesContext";
import LinearLogo from "../../images/linearLogo.png";
import { INTEGRATIONS } from "../../utils/constants";
import {
  AutocompleteOption,
  IntegrationSettings,
  SettingsTabNames,
} from "../../utils/interfaces";
import IntegrationButton from "../IntegrationButton";
import LinearSection from "./LinearSection";
import Section from "./Section";

const { LINEAR, LINEAR_OAUTH_URI } = INTEGRATIONS;

type EngineeringTicketPlatformIntegrationType =
  GetEngineeringTicketPlatformIntegrationQuery["getEngineeringTicketPlatformIntegration"];

interface Props {
  section: string;
  tabName: SettingsTabNames;
  discardName: string;
  companyId?: number;
  clientId?: string;
  data?: EngineeringTicketPlatformIntegrationType;
}

const LinearIntegrationSection: React.FC<Props> = ({
  section,
  tabName,
  discardName,
  companyId,
  clientId,
  data,
}) => {
  const [settings, setSettings] = useState<IntegrationSettings>({
    pushAutoIdentifiedIssues: false,
    defaultPriority: 0,
    defaultProject: "",
    defaultTeam: "",
  });

  const originalSettingsRef = useRef(settings);
  const [updateIntegration] =
    useUpdateEngineeringTicketPlatformIntegrationMutation();
  const {
    setExternalSaveFunction,
    setExternalFunction,
    isDirty,
    markSettingAsChanged,
    clearUnsavedChanges,
  } = useUnsavedChanges();

  const linearProjects: AutocompleteOption[] = data?.projects || [];
  const linearTeams: AutocompleteOption[] = data?.teams || [];
  const linearInstalled = data?.installed;

  const handleInputChange = (field: keyof typeof settings, value: any) => {
    setSettings((prev) => {
      const updated = { ...prev, [field]: value };
      if (prev[field] !== value) markSettingAsChanged(tabName);
      return updated;
    });
  };

  const handleSave = useCallback(async () => {
    if (!isDirty(tabName) || !linearInstalled) return;

    try {
      await updateIntegration({
        variables: {
          input: { provider: LINEAR, ...settings },
          companyId,
        },
      });
      originalSettingsRef.current = settings;
      clearUnsavedChanges(tabName);
    } catch (err) {
      console.error("Linear save error:", err);
    }
  }, [
    isDirty,
    linearInstalled,
    updateIntegration,
    settings,
    companyId,
    clearUnsavedChanges,
  ]);

  const handleDiscard = useCallback(async () => {
    setSettings(originalSettingsRef.current);
    clearUnsavedChanges(tabName);
  }, [clearUnsavedChanges]);

  useEffect(() => {
    if (data) {
      const {
        installed,
        pushAutoIdentifiedIssues,
        defaultPriority,
        defaultProject,
        defaultTeam,
      } = data;

      if (!installed) return;

      const newSettings = {
        pushAutoIdentifiedIssues,
        defaultPriority,
        defaultProject,
        defaultTeam,
      };

      setSettings(newSettings);
      originalSettingsRef.current = newSettings;
    }
  }, [data]);

  useEffect(() => {
    if (section === LINEAR.toLowerCase()) {
      setExternalSaveFunction(tabName, handleSave);
      setExternalFunction(discardName, handleDiscard);
    }
  }, [
    section,
    handleSave,
    handleDiscard,
    setExternalSaveFunction,
    setExternalFunction,
  ]);

  return (
    <Section title={LINEAR} hide={section !== LINEAR.toLowerCase()}>
      {linearInstalled ? (
        <LinearSection
          settings={settings}
          linearProjects={linearProjects}
          linearTeams={linearTeams}
          handleInputChange={handleInputChange}
        />
      ) : (
        <div className="flex items-center">
          <IntegrationButton
            logo={LinearLogo}
            name={LINEAR}
            to={`${LINEAR_OAUTH_URI}&client_id=${clientId}`}
          />
        </div>
      )}
    </Section>
  );
};

export default LinearIntegrationSection;
